import { Attribute } from './Attribute.js';

export const CYBER_FILTER_LICENSE_LIST_ATTRIBUTE_TYPE = 'CyberFilterLicenseListAttribute';
export const CYBER_FILTER_LICENSE_LIST_ATTRIBUTE_ID = 'LicenceList';

/**
 *
 * @property {{id, state: ('ACTIVE'|'ACTIVATING'|'DEACTIVATING'|'NEW'), date, flag: ('Y'|'N')}[]} licenses
 * @property {boolean} isReadOnly
 */
export class CyberFilterLicenseListAttribute extends Attribute {
  constructor({ licenses, isReadOnly = false }) {
    super();
    this.licenses = licenses;
    this.isReadOnly = isReadOnly;
    this.type = CYBER_FILTER_LICENSE_LIST_ATTRIBUTE_TYPE;
  }

  /**
   *
   * @returns {{id, state: ('ACTIVE'|'ACTIVATING'|'DEACTIVATING'|'NEW'), date, flag: ('Y'|'N')}[]}
   */
  get activeLicenses() {
    return this.licenses.filter(license => license.flag === 'Y');
  }

  /**
   *
   * @returns {{id, state: ('ACTIVE'|'ACTIVATING'|'DEACTIVATING'|'NEW'), date, flag: ('Y'|'N')}[]}
   */
  get inactiveLicenses() {
    return this.licenses.filter(license => license.flag === 'N');
  }

  /**
   * Modifies list of licenses to have count of active licenses corresponding to provided amount.
   *
   * Disabled licenses are enabled prior to adding new "empty" licenses.
   *
   * @param {number} amount
   * @return {void}
   */
  setNumberOfActiveLicenses(amount) {
    if (amount === this.activeLicenses.length) {
      return;
    }

    const totalNumberOfLicensesToBeChanged = amount - this.activeLicenses.length;
    if (totalNumberOfLicensesToBeChanged < 0) {
      // disable/remove new licenses
      const amountToDeactivate = Math.min(
        Math.abs(totalNumberOfLicensesToBeChanged),
        this.activeLicenses.length,
      );

      this.deactivateLicenses(amountToDeactivate);
    } else {
      // enable/add new licenses
      const amountToActivate = Math.min(
        totalNumberOfLicensesToBeChanged,
        this.inactiveLicenses.length,
      );
      const amountToAdd = totalNumberOfLicensesToBeChanged - amountToActivate;

      this.activateLicenses(amountToActivate);
      this.addNewLicenses(amountToAdd);
    }
  }

  /**
   *
   * @param {number} count - number of licenses to deactivate
   * @returns {void}
   */
  deactivateLicenses(count) {
    this.activeLicenses.slice(-count).forEach(license => {
      // eslint-disable-next-line no-param-reassign
      license.flag = 'N';
    });
  }

  /**
   *
   * @param {number} count - number of licenses to activate
   * @returns {void}
   */
  activateLicenses(count) {
    this.inactiveLicenses.slice(-count).forEach(license => {
      // eslint-disable-next-line no-param-reassign
      license.flag = 'Y';
    });
  }

  /**
   *
   * @param {number} count - number of new "empty" licenses to add
   * @returns {void}
   */
  addNewLicenses(count) {
    this.licenses.push(
      ...Array(count).fill({
        id: null,
        state: 'NEW',
        date: null,
        flag: 'Y',
      }),
    );
  }
}
